import type { CSSProperties } from 'react';
import { useStore, ViewportPortal } from '@xyflow/react';
import { IconButton, Icon, Tooltip, Divider, icon } from '@nula/core-ui';
import { useNodeActions } from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import {
  MULTI_ACTIONS,
  type MultiAction,
  type MultiActionContext,
} from '../../wiring/actions/multi-actions';
import styles from './node-floating-toolbar.module.css';
import msStyles from './multi-selection-toolbar.module.css';

interface SelectedNodeInfo {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Action IDs surfaced on the multi-selection floating toolbar.
 * STRICT SUBSET of MULTI_ACTIONS — Copy stays menu-only, since the
 * floating toolbar's small footprint over a multi-selection bounding
 * box reserves space for the more useful one-click gestures
 * (Group + Duplicate + Delete). Copy is still available via right-click.
 *
 * Group is the first entry — same registry-declared order — so the
 * "creative" gesture sits before the destructive cluster.
 */
const TOOLBAR_MULTI_ACTION_IDS: ReadonlySet<MultiAction['id']> = new Set([
  'group',
  'duplicate',
  'delete',
]);

/**
 * Renders a single shared context toolbar above the bounding box of
 * all currently-selected nodes, via ViewportPortal (flow-space
 * coordinates). Only mounts when 2+ nodes are selected.
 *
 * Action cluster sourced from MULTI_ACTIONS, filtered through
 * TOOLBAR_MULTI_ACTION_IDS so the registry is the single source of
 * truth for label + icon + handler dispatch.
 */
export function MultiSelectionToolbar() {
  const actions = useNodeActions();

  const selected = useStore((s) => {
    const result: SelectedNodeInfo[] = [];
    s.nodeLookup.forEach((internal) => {
      if (!internal.selected) return;
      const abs = internal.internals?.positionAbsolute;
      if (!abs) return;
      result.push({
        id: internal.id,
        x: abs.x,
        y: abs.y,
        width: internal.measured?.width ?? 0,
        height: internal.measured?.height ?? 0,
      });
    });
    return result;
  });

  if (!actions || selected.length < 2) return null;

  const minX = Math.min(...selected.map((n) => n.x));
  const minY = Math.min(...selected.map((n) => n.y));
  const maxX = Math.max(...selected.map((n) => n.x + n.width));
  const centerX = (minX + maxX) / 2;

  const ids = selected.map((n) => n.id);

  // Build the registry context. Copy isn't in the toolbar's subset —
  // the no-op stub never fires because TOOLBAR_MULTI_ACTION_IDS
  // filters it before render.
  const ctx: MultiActionContext = {
    ids,
    onCopyMany: () => {},
    onDuplicateMany: actions.onDuplicateMany,
    onDeleteMany: actions.onDeleteMany,
    onGroupMany: actions.onGroupMany,
  };

  const visibleActions = MULTI_ACTIONS
    .filter((a) => TOOLBAR_MULTI_ACTION_IDS.has(a.id))
    .filter((a) => a.available(ctx));

  return (
    <ViewportPortal>
      <div
        data-testid="multi-selection-toolbar"
        className={msStyles.root}
        style={{
          '--ms-toolbar-x': `${centerX}px`,
          '--ms-toolbar-y': `${minY}px`,
        } as CSSProperties}
      >
        {visibleActions.flatMap((action, idx) => {
          const isLast = idx === visibleActions.length - 1;
          // Flat-map so the React parent is the toolbar root, not a
          // <span> wrapper — keeps the rendered DOM identical to the
          // hand-rolled version (no extra elements between root and
          // buttons/dividers) and avoids the no-inline-style rule.
          const button = (
            <Tooltip key={`${action.id}-btn`} title={action.label} placement="top">
              <IconButton
                transparent
                aria-label={action.label}
                className={styles.btn}
                onClick={() => action.run(ctx)}
              >
                <Icon name={action.icon} size={icon.md} />
              </IconButton>
            </Tooltip>
          );
          // Divider between non-last entries — preserves the same
          // visual structure the hand-rolled version had (one
          // divider between Duplicate and Delete).
          if (isLast) return [button];
          return [
            button,
            <Divider
              key={`${action.id}-div`}
              orientation="vertical"
              className={styles.divider}
            />,
          ];
        })}
      </div>
    </ViewportPortal>
  );
}
